import { useRef } from 'react';
import { Alert, Button, Input, InputNumber, Select, Space } from 'antd';
import { MeasurementControl, InputSelect, InputSwitch, optionSearchFilter } from '../Input';
import { MAX_MEASURES, MAX_OWN_UNITS, MAX_PRECISION, allowedMeasures, getMeasure, getUnit, isMeasurementValue, isOwnUnitId, isUnitOf, measureOfUnit, ownUnitIdIn, pluralOf, settleOwnUnits, unitsOf } from '../../utils/units';
import { MAX_DISPLAY_RULES, applyMeasurePicker, defaultUnitFor, groupUnitOptions, measurePickerOptions, measurePickerValue, perOptionsFor, perWord, ruleBound, unitOptionsFor, unitSymbol } from '../../utils/measurements';
import { CLOSE, PLUS } from '../icons';
import ConversionUnitField from './ConversionUnitField';
import { explicitDefault, storedDefaultProblem } from '../../utils/elementDefaults';
import { SHAPE_INSTRUCTION_KEY } from '../../utils/shapeChange';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

/**
 * The card-definition sub-editor for a `measurement` element's options —
 * everything the shared formatter and normaliser read (docs/plans/
 * measurements.md, "Data model"):
 *
 *   measure        which unit table (mass, length, ...). Picking a different
 *                  measure resets every unit-bearing option below, since a
 *                  pound is not a length. A RATE (volume, mass or count per
 *                  time — its own measure, `volume-rate`, …) is picked as
 *                  the measure it counts plus the Per beside it; turning
 *                  Per on or off is a measure change, and moving it to
 *                  another time moves every unit below to that time
 *                  (utils/measurements applyMeasurePicker).
 *   defaultUnit    the entry default AND what a bare number means.
 *   units          the entry allowlist; empty = every unit of the measure.
 *                  Always includes the default unit (the API refuses an
 *                  allowlist without it, so it is added rather than refused).
 *   min / max      bounds in the default unit.
 *   display        { unit, precision } — the unit a value is SHOWN in
 *                  ('entered' = as typed, 'auto' = largest unit that is at
 *                  least 1, within the default unit's own system and
 *                  family) and its decimals.
 *   displayRules   up to five "when the value is below 20 lb, show as lb-oz
 *                  with 0 decimals" rows, first match wins.
 *   measures       (KPR-1085) "Allow several measures": every measure the
 *                  element allows, the primary (`measure`) first — a recipe's
 *                  amount in volume, mass or count. Off by default; on, the
 *                  "Also allow" picker names the others, the allowlist spans
 *                  them, and min / max / display rules (written in the default
 *                  unit, so about the primary alone) are off and hidden. Taking
 *                  a measure away from a saved element whose items hold values
 *                  in it is the save's B16 question (MultiToggleDialog, clear).
 *   ownUnits       (KPR-1109) "Own units": units the author names (a smidgen,
 *                  a dollop), each a singular and a plural, at most
 *                  MAX_OWN_UNITS. A new unit's id is minted from its singular
 *                  when the field is left (the vendored ownUnitIdIn, the
 *                  API's own rule): the saved unit of that name when no other
 *                  row holds its id (one removed and added back), else a new
 *                  id clear of every id the rows and the SAVED list
 *                  (`savedOwnUnits`) hold -- never a removed unit's, whose
 *                  values would read as the new one's without the save's
 *                  question. Its plural is filled when empty; a rename keeps
 *                  the id. Like several measures, they take min, max and
 *                  display rules away. A name the parser reads as a built-in
 *                  unit, or one two units share, is said under the list (the
 *                  API refuses it on save). Removing one that items hold is the
 *                  save's B16 question (MultiToggleDialog: clear; moving them
 *                  to another unit waits for the Change card mapping step).
 *   defaultValue   { value, unit } for new items, through the same control
 *                  the entry form uses.
 *   convertFrom    { unit } — only while a SAVED numeric element is being
 *                  switched to measurement (`conversion` prop, planned by
 *                  utils/conversions.js): the unit its existing values are
 *                  in. A request-time instruction the API never stores.
 *
 * Writes go through the form's path-based onChange (`elements[i].options.x`)
 * so this editor holds no state of its own. The API's `measure_immutable`
 * refusal (the measure changed while items hold values) is shown here,
 * against the element it names, rather than only as a toast.
 */

const COMPARATORS = [
	{ value: 'lt', label: 'below' },
	{ value: 'lte', label: 'at most' },
	{ value: 'gt', label: 'above' },
	{ value: 'gte', label: 'at least' },
];

const DISPLAY_MODES = [
	{ value: 'auto', label: 'Automatic (largest unit in the same family that fits)', searchText: 'automatic auto' },
	{ value: 'entered', label: 'As entered', searchText: 'as entered' },
];

const MEASURE_OPTIONS = measurePickerOptions();

// The keys a measure change wipes: every one that names a unit or a bound
// expressed in one.
const MEASURE_SCOPED_KEYS = ['defaultUnit', 'units', 'min', 'max', 'display', 'displayRules', 'defaultValue', 'convertFrom'];

const finiteOrUndefined = v => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

export default function MeasurementOptions({ index, element, onChange, saveError = null, conversion = null, basePath = null, savedOwnUnits = null }) {
	const options = element?.options || {};
	const prefix = `${basePath || `elements[${index}]`}.options`;
	const measure = getMeasure(options.measure);
	const picked = measurePickerValue(options);
	const perOptions = perOptionsFor(picked.measure);
	const per = measure?.rateOf ? picked.per : null;
	const defaultUnit = defaultUnitFor(options);
	const symbol = unitSymbol(defaultUnit);
	// A rate's default, display and rule units are offered at its own time;
	// the allowlist may name any time. A unit already chosen outside that
	// list is kept in it, so a Select never shows a bare id.
	const unitOptions = measure ? unitOptionsFor(measure.id, per) : [];
	const allUnitOptions = measure && Array.isArray(options.measures) && options.measures.length > 1
		? groupUnitOptions(allowedMeasures(options).flatMap(id => unitsOf(id)))
		: (measure && per ? unitOptionsFor(measure.id) : unitOptions);
	const withChosen = (list, unitId) => (per && isUnitOf(unitId, measure.id) && getUnit(unitId).per !== per
		? [...list, ...groupUnitOptions([getUnit(unitId)])]
		: list);
	// Several measures (KPR-1085): the list is present while the switch is on.
	const several = Array.isArray(options.measures);
	// Own units (KPR-1109): like several measures, they take min, max and
	// display rules away (each is a measure of one unit).
	const ownUnits = Array.isArray(options.ownUnits) ? options.ownUnits : [];
	const owned = ownUnits.length > 0;
	// Read against the saved list: a unit saved unchanged is not asked the
	// built-in question again (the API's rule), whatever the registry grew to.
	const ownProblem = owned ? settleOwnUnits(ownUnits, savedOwnUnits).problem : null;
	const measures = allowedMeasures(options);
	const others = several ? (options.measures || []).filter(id => id !== options.measure) : [];
	const allowlist = Array.isArray(options.units) ? options.units.filter(id => measures.includes(measureOfUnit(id))) : [];
	const display = options.display && typeof options.display === 'object' ? options.display : {};
	const rules = Array.isArray(options.displayRules) ? options.displayRules : [];

	const immutable = saveError?.code === 'measure_immutable' && (!saveError.element || saveError.element === element?.name);
	// The default's own line (B12): the save's refusal of THIS element's
	// default, else the same question asked live — DefaultValueField's rule.
	const defaultIsSet = explicitDefault(element) !== undefined;
	const defaultRefusal = saveError?.code === 'invalid_options' && saveError.option === 'defaultValue' && saveError.element === element?.name
		? String(saveError.message || '').replace(/^elements\.[^:]+:\s*/, '')
		: '';
	const defaultProblem = defaultRefusal || storedDefaultProblem(element);

	// Another measure keeps the Per it had when the new measure has a rate,
	// and the other measures it allows (the old primary leaves with the units).
	const setMeasure = (_, measureId) => {
		const next = applyMeasurePicker(options, measureId, picked.per, MEASURE_SCOPED_KEYS);
		if (several && next.measure) next.measures = [next.measure, ...others.filter(id => id !== next.measure && id !== options.measure)];
		onChange(prefix, next);
	};

	// Min, max and display rules the switch took away, kept while this
	// editor is open so turning it back off in the same draft returns them.
	const putAside = useRef(null);

	// "Allow several measures": on lists the primary alone until others are
	// picked (the API reads a list of one as one measure), and takes min, max
	// and display rules away — they speak in the default unit. Off drops the
	// list, and the allowlist's units and a default value of the other
	// measures with it, and gives back what turning it on put aside. Either
	// way a "clear" already answered goes: it was said about the measures as
	// they were, so the next save asks about the measures as they are.
	const putAwayBounds = (next) => {
		const aside = {};
		['min', 'max', 'displayRules'].forEach((key) => {
			if (next[key] !== undefined) aside[key] = next[key];
			delete next[key];
		});
		putAside.current = Object.keys(aside).length ? aside : null;
	};
	const giveBackBounds = (next) => {
		if (putAside.current) Object.assign(next, putAside.current);
		putAside.current = null;
	};
	const setSeveral = (_, checked) => {
		if (!measure) return;
		const next = { ...options };
		delete next[SHAPE_INSTRUCTION_KEY];
		if (checked) {
			next.measures = [measure.id];
			// Own units already put them aside.
			if (!owned) putAwayBounds(next);
		} else {
			delete next.measures;
			if (Array.isArray(next.units)) {
				const kept = next.units.filter(id => isUnitOf(id, measure.id));
				if (kept.length) next.units = kept;
				else delete next.units;
			}
			if (isMeasurementValue(next.defaultValue) && !isUnitOf(next.defaultValue.unit, measure.id) && !isOwnUnitId(next.defaultValue.unit)) delete next.defaultValue;
			// Still with own units, they stay away.
			if (!owned) giveBackBounds(next);
		}
		onChange(prefix, next);
	};

	// Own units (KPR-1109). The whole list is written at once; the first one
	// puts min, max and display rules aside as several measures do, and the
	// last one gives them back. A default value in a unit taken away goes, and
	// a "clear" already answered goes too (it was said about the list as it was).
	const writeOwnUnits = (list) => {
		const next = { ...options };
		delete next[SHAPE_INSTRUCTION_KEY];
		if (list.length) next.ownUnits = list;
		else delete next.ownUnits;
		if (list.length && !owned && !several) putAwayBounds(next);
		if (!list.length && owned && !several) giveBackBounds(next);
		if (isMeasurementValue(next.defaultValue) && isOwnUnitId(next.defaultValue.unit) && !list.some(own => own.id === next.defaultValue.unit)) delete next.defaultValue;
		onChange(prefix, next);
	};
	const addOwnUnit = () => {
		if (ownUnits.length >= MAX_OWN_UNITS) return;
		writeOwnUnits([...ownUnits, { singular: '', plural: '' }]);
	};
	const updateOwnUnit = (i, patch) => writeOwnUnits(ownUnits.map((own, idx) => (idx === i ? { ...own, ...patch } : own)));
	const removeOwnUnit = i => writeOwnUnits(ownUnits.filter((_, idx) => idx !== i));
	// Leaving a singular: a new unit gets its id (the API's own minting rule,
	// clear of every id the rows and the saved list hold) and an empty plural
	// its suggestion.
	const settleOwnUnit = (i) => {
		const own = ownUnits[i];
		const singular = typeof own?.singular === 'string' ? own.singular.trim() : '';
		if (!singular) return;
		const patch = {};
		if (!own.id) patch.id = ownUnitIdIn(singular, ownUnits.filter((_, idx) => idx !== i).map(other => other.id).filter(Boolean), savedOwnUnits);
		if (!(typeof own.plural === 'string' && own.plural.trim())) patch.plural = pluralOf(singular);
		if (Object.keys(patch).length) updateOwnUnit(i, patch);
	};
	// The other measures: at most MAX_MEASURES in all (the API's cap). A
	// measure taken out takes its allowlist units and a default value in it
	// along, and drops a "clear" already answered (see setSeveral).
	const setOthers = (ids) => {
		const picked = (ids || []).filter(id => id !== options.measure && getMeasure(id)).slice(0, MAX_MEASURES - 1);
		const keeps = [options.measure, ...picked];
		const next = { ...options, measures: keeps };
		delete next[SHAPE_INSTRUCTION_KEY];
		if (allowlist.length) {
			const kept = allowlist.filter(id => keeps.includes(measureOfUnit(id)));
			if (kept.length) next.units = kept;
			else delete next.units;
		}
		// A default in one of the element's own units (KPR-1109) is not a measure's.
		if (isMeasurementValue(next.defaultValue) && !isOwnUnitId(next.defaultValue.unit) && !keeps.includes(measureOfUnit(next.defaultValue.unit))) delete next.defaultValue;
		onChange(prefix, next);
	};
	const otherMeasureOptions = MEASURE_OPTIONS.filter(option => option.value !== options.measure);
	const setPer = (_, nextPer) => onChange(prefix, applyMeasurePicker(options, picked.measure, nextPer, MEASURE_SCOPED_KEYS));

	const setDefaultUnit = (_, unitId) => {
		if (!unitId) return;
		onChange(`${prefix}.defaultUnit`, unitId);
		if (allowlist.length && !allowlist.includes(unitId)) {
			onChange(`${prefix}.units`, [...allowlist, unitId]);
		}
	};

	const setAllowed = (ids) => {
		const list = (ids || []).filter(id => measures.includes(measureOfUnit(id)));
		if (list.length && defaultUnit && !list.includes(defaultUnit)) list.push(defaultUnit);
		onChange(`${prefix}.units`, list.length ? list : undefined);
	};

	const setBound = (key, v) => onChange(`${prefix}.${key}`, finiteOrUndefined(v));

	const setDisplay = (patch) => {
		const next = { ...display, ...patch };
		Object.keys(next).forEach(key => { if (next[key] === undefined) delete next[key]; });
		onChange(`${prefix}.display`, Object.keys(next).length ? next : undefined);
	};

	const setRules = (nextRules) => onChange(`${prefix}.displayRules`, nextRules.length ? nextRules : undefined);

	const updateRule = (i, patch) => setRules(rules.map((rule, idx) => (idx === i ? { ...rule, ...patch } : rule)));

	const addRule = () => {
		if (rules.length >= MAX_DISPLAY_RULES) return;
		setRules([...rules, { when: { lt: 1 }, unit: defaultUnit, precision: 0 }]);
	};

	const removeRule = (i) => setRules(rules.filter((_, idx) => idx !== i));

	const displayUnitOptions = [...DISPLAY_MODES, ...withChosen(unitOptions, display.unit)];

	return (
		<div className='measurement-options'>
			<div className='micro-label card-def-element__section'>Measurement</div>

			{immutable ? (
				<Alert
					type='error'
					showIcon
					className='mb-2'
					message='The measure cannot change'
					description={saveError.message || 'Items already hold values for this element; pick the measure and Per it was saved with, or add a new element for the other measure.'}
				/>
			) : null}

			<div className='measurement-options__row'>
				<div className='measurement-options__cell measurement-options__cell--wide'>
					<InputSelect
						elementName={`${prefix}.measure`}
						elementLabel='Measure'
						value={picked.measure || undefined}
						onChange={setMeasure}
						options={MEASURE_OPTIONS}
						placeholder='What is measured'
						allowClear={false}
						required={true}
					/>
				</div>
				{perOptions.length ? (
					<div className='measurement-options__cell'>
						<InputSelect
							elementName={`${prefix}.per`}
							elementLabel='Per'
							value={picked.per}
							onChange={setPer}
							options={perOptions}
							allowClear={false}
							help='A rate is an amount per unit of time, like mL per hour.'
						/>
					</div>
				) : null}
			</div>

			{measure ? <>
				<InputSwitch
					elementName={`${prefix}.measuresOn`}
					elementLabel='Allow several measures'
					description={`For an amount that is a volume on one item and a weight or a count on the next, like a recipe's ingredients. Each value keeps its own unit, and values are compared only within one measure.`}
					value={several}
					onChange={setSeveral}
					first={true}
				/>
				{several ? (
					<div className='mb-2'>
						<label className='d-block' htmlFor={`${prefix}.measures`}>Also allow</label>
						<Select
							id={`${prefix}.measures`}
							mode='multiple'
							size='large'
							className='w-100'
							value={others}
							onChange={setOthers}
							options={otherMeasureOptions}
							filterOption={optionSearchFilter}
							placeholder='Pick the other measures'
							aria-label='Also allow'
							maxCount={MAX_MEASURES - 1}
						/>
						<div className='measurement-options__hint'>
							{`Up to ${MAX_MEASURES} measures in all. Minimum, maximum and display rules are written in ${symbol || 'the default unit'}, so they are off while several measures are allowed.`}
						</div>
					</div>
				) : null}
				<InputSelect
					elementName={`${prefix}.defaultUnit`}
					elementLabel='Default unit'
					value={defaultUnit}
					onChange={setDefaultUnit}
					options={withChosen(unitOptions, defaultUnit)}
					allowClear={false}
					required={true}
					help='The unit new values start in, and what a bare number means.'
				/>

				{/* A saved number element becoming a measurement: what its stored
				    numbers mean. Absent for new elements and unchanged types. */}
				{conversion?.key === 'convertFrom' ? (
					<ConversionUnitField
						index={index}
						elementName={element?.name}
						plan={conversion}
						onChange={onChange}
						saveError={saveError}
					/>
				) : null}

				<div className='mb-2'>
					<label className='d-block' htmlFor={`${prefix}.units`}>Allowed units</label>
					<Select
						id={`${prefix}.units`}
						mode='multiple'
						size='large'
						className='w-100'
						value={allowlist}
						onChange={setAllowed}
						options={allUnitOptions}
						filterOption={optionSearchFilter}
						placeholder={several && others.length ? 'Every unit of these measures' : (per ? `Every unit ${perWord(per)}` : 'Every unit of the measure')}
						aria-label='Allowed units'
					/>
				</div>

				<div className='mb-2 own-units'>
					<label className='d-block' htmlFor={`${prefix}.ownUnits.0.singular`}>Own units</label>
					<div className='measurement-options__hint own-units__hint'>
						For a unit keepr has no name for, like a smidgen. A value in one is counted, never converted, and compared only with values in that unit.
					</div>
					{owned ? (
						<div className='own-units__list'>
							{ownUnits.map((own, i) => (
								// Keyed by place: the id a new row is given when its singular
								// is left must not remount the row and lose the focus.
								// eslint-disable-next-line react/no-array-index-key
								<div className='own-units__row' key={`own-unit-${i}`} data-testid='own-unit'>
									<Input
										{...NO_TEXT_ASSIST}
										id={`${prefix}.ownUnits.${i}.singular`}
										size='large'
										value={own.singular ?? ''}
										onChange={event => updateOwnUnit(i, { singular: event.target.value })}
										onBlur={() => settleOwnUnit(i)}
										placeholder='Singular, like smidgen'
										maxLength={40}
										aria-label={`Own unit ${i + 1}, singular`}
									/>
									<Input
										{...NO_TEXT_ASSIST}
										id={`${prefix}.ownUnits.${i}.plural`}
										size='large'
										value={own.plural ?? ''}
										onChange={event => updateOwnUnit(i, { plural: event.target.value })}
										placeholder={own.singular ? pluralOf(own.singular) : 'Plural'}
										maxLength={40}
										aria-label={`Own unit ${i + 1}, plural`}
									/>
									<Button
										type='text'
										size='large'
										icon={<CLOSE />}
										onClick={() => removeOwnUnit(i)}
										aria-label={`Remove ${own.singular ? `own unit ${own.singular}` : `own unit ${i + 1}`}`}
									/>
								</div>
							))}
						</div>
					) : null}
					{ownProblem ? (
						<div className='element-field__error' role='alert'>
							{ownProblem.index !== undefined ? `Own unit ${ownProblem.index + 1}: ` : ''}{ownProblem.code === 'empty' ? 'needs a name' : ownProblem.message}.
						</div>
					) : null}
					<Space wrap>
						<Button icon={<PLUS />} onClick={addOwnUnit} disabled={ownUnits.length >= MAX_OWN_UNITS}>
							Add own unit
						</Button>
						{ownUnits.length >= MAX_OWN_UNITS ? (
							<span className='measurement-options__hint'>Up to {MAX_OWN_UNITS} own units.</span>
						) : null}
						{owned && !several ? (
							<span className='measurement-options__hint'>{`Minimum, maximum and display rules are written in ${symbol || 'the default unit'}, so they are off while the element has its own units.`}</span>
						) : null}
					</Space>
				</div>

				{several || owned ? null : <div className='measurement-options__row'>
					<div className='mb-2 measurement-options__cell'>
						<label className='d-block' htmlFor={`${prefix}.min`}>Minimum</label>
						<InputNumber
							id={`${prefix}.min`}
							size='large'
							className='w-100'
							value={finiteOrUndefined(options.min) ?? null}
							onChange={v => setBound('min', v)}
							suffix={<span className='input-measurement__symbol'>{symbol}</span>}
							inputMode='decimal'
							controls={false}
							aria-label='Minimum'
						/>
					</div>
					<div className='mb-2 measurement-options__cell'>
						<label className='d-block' htmlFor={`${prefix}.max`}>Maximum</label>
						<InputNumber
							id={`${prefix}.max`}
							size='large'
							className='w-100'
							value={finiteOrUndefined(options.max) ?? null}
							onChange={v => setBound('max', v)}
							suffix={<span className='input-measurement__symbol'>{symbol}</span>}
							inputMode='decimal'
							controls={false}
							aria-label='Maximum'
						/>
					</div>
				</div>}

				<div className='measurement-options__row'>
					<div className='mb-2 measurement-options__cell measurement-options__cell--wide'>
						<label className='d-block' htmlFor={`${prefix}.display.unit`}>Display unit</label>
						<Select
							id={`${prefix}.display.unit`}
							size='large'
							className='w-100'
							value={display.unit || defaultUnit}
							onChange={unit => setDisplay({ unit })}
							options={displayUnitOptions}
							filterOption={optionSearchFilter}
							showSearch
							aria-label='Display unit'
						/>
					</div>
					<div className='mb-2 measurement-options__cell'>
						<label className='d-block' htmlFor={`${prefix}.display.precision`}>Decimals</label>
						<InputNumber
							id={`${prefix}.display.precision`}
							size='large'
							className='w-100'
							min={0}
							max={MAX_PRECISION}
							step={1}
							precision={0}
							value={finiteOrUndefined(display.precision) ?? null}
							onChange={v => setDisplay({ precision: finiteOrUndefined(v) })}
							placeholder='1'
							inputMode='numeric'
							aria-label='Decimals'
						/>
					</div>
				</div>

				{several || owned ? null : <div className='mb-2'>
					<div className='field-label-row'>
						<label className='d-block'>Display rules</label>
					</div>
					{rules.length ? (
						<div className='measurement-options__rules'>
							{rules.map((rule, i) => {
								const [comparator, bound] = ruleBound(rule?.when) || ['lt', null];
								return (
									<div className='measurement-options__rule' key={i} data-testid='display-rule'>
										<span className='measurement-options__word'>When value is</span>
										<Select
											size='middle'
											className='measurement-options__comparator'
											value={comparator}
											onChange={op => updateRule(i, { when: { [op]: bound ?? 0 } })}
											options={COMPARATORS}
											popupMatchSelectWidth={false}
											aria-label={`Rule ${i + 1} comparison`}
										/>
										<InputNumber
											size='middle'
											className='measurement-options__bound'
											value={bound}
											onChange={v => updateRule(i, { when: { [comparator]: finiteOrUndefined(v) ?? 0 } })}
											suffix={<span className='input-measurement__symbol'>{symbol}</span>}
											inputMode='decimal'
											controls={false}
											aria-label={`Rule ${i + 1} threshold`}
										/>
										<span className='measurement-options__word'>show as</span>
										<Select
											size='middle'
											className='measurement-options__rule-unit'
											value={isUnitOf(rule?.unit, options.measure) ? rule.unit : defaultUnit}
											onChange={unit => updateRule(i, { unit })}
											options={withChosen(unitOptions, rule?.unit)}
											filterOption={optionSearchFilter}
											showSearch
											popupMatchSelectWidth={false}
											aria-label={`Rule ${i + 1} unit`}
										/>
										<span className='measurement-options__word'>with</span>
										<InputNumber
											size='middle'
											className='measurement-options__precision'
											min={0}
											max={MAX_PRECISION}
											step={1}
											precision={0}
											value={finiteOrUndefined(rule?.precision) ?? null}
											onChange={v => updateRule(i, { precision: finiteOrUndefined(v) })}
											inputMode='numeric'
											aria-label={`Rule ${i + 1} decimals`}
										/>
										<span className='measurement-options__word'>decimals</span>
										<Button
											type='text'
											icon={<CLOSE />}
											onClick={() => removeRule(i)}
											aria-label={`Remove rule ${i + 1}`}
										/>
									</div>
								);
							})}
						</div>
					) : null}
					<Space>
						<Button
							icon={<PLUS />}
							onClick={addRule}
							disabled={rules.length >= MAX_DISPLAY_RULES}
						>
							Add rule
						</Button>
						{rules.length >= MAX_DISPLAY_RULES ? (
							<span className='measurement-options__hint'>Up to {MAX_DISPLAY_RULES} rules.</span>
						) : null}
					</Space>
				</div>}

				<div className='mb-2 default-value-field'>
					<div className='default-value-field__head'>
						<label className='d-block' htmlFor={`${prefix}.defaultValue`}>Default value (new items)</label>
						{defaultIsSet ? (
							<Button type='link' size='small' className='default-value-field__clear' onClick={() => onChange(`${prefix}.defaultValue`, undefined)}>
								Clear default
							</Button>
						) : null}
					</div>
					{/* Only a { value, unit } default seeds new items — and,
					    since the owner's ruling B12, is applied by the server on
					    every create — while it fits the measure, the units and
					    min / max (utils/elementDefaults). A bare number left over
					    from the element's numeric days is not shown as one; the
					    problem line says so and Clear default removes it. */}
					<MeasurementControl
						id={`${prefix}.defaultValue`}
						value={isMeasurementValue(options.defaultValue) ? options.defaultValue : null}
						onChange={next => onChange(`${prefix}.defaultValue`, next || undefined)}
						element={element}
						cardId='card-definition'
						aria-label='Default value (new items)'
					/>
					{defaultProblem ? <div className='element-field__error' role='alert'>{defaultProblem}</div> : null}
				</div>
			</> : null}
		</div>
	);
}
